import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { useLab } from '../i18n/LabContext';
import { useLang } from '../i18n/LanguageContext';

function getGenres(t) {
  return [
    { key: 'action',   label: '⚔️ Action' },
    { key: 'strategy', label: '🧠 Strategy' },
    { key: 'sports',   label: '⚽ Sports' },
    { key: 'arcade',   label: '🕹️ Arcade' },
    { key: 'rpg',      label: '🧙 RPG' },
    { key: 'racing',   label: '🏎️ Racing' },
    { key: 'fighting', label: '🥊 Fighting' },
    { key: 'other',    label: t('genreOther') },
  ];
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const BASE_URL = API_URL.replace('/api', '');

function isSuperAdmin() {
  try {
    const p = JSON.parse(atob(localStorage.getItem('token').split('.')[1]));
    return p?.sa === true;
  } catch { return false; }
}

export default function Games() {
  const { t, lang } = useLang();
  const { labId: ctxLabId } = useLab();
  const labId = ctxLabId ? String(ctxLabId) : '';
  const [games, setGames]       = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editGame, setEditGame] = useState(null);
  const [form, setForm]         = useState({ name: '', genre: 'action', exe_path: '', short_description: '', description: '', sort_order: 0 });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving]     = useState(false);
  const [preview, setPreview]   = useState(null);
  const [catalogModal, setCatalogModal] = useState(false);
  const [catalogList, setCatalogList]   = useState([]);
  const [catalogSel, setCatalogSel]     = useState({});
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogImporting, setCatalogImporting] = useState(false);
  const [catalogFilter, setCatalogFilter] = useState({ platform: '', genre: '', q: '' });
  const [catalogUploading, setCatalogUploading] = useState({});

  const trailerRefs = useRef({});
  const coverRefs   = useRef({});
  const heroRefs    = useRef({});

  const GENRES = getGenres(t);

  function genreLabel(key) {
    return GENRES.find(g => g.key === key)?.label || key;
  }

  useEffect(() => { loadGames(); }, []);

  const loadGames = () => {
    api.get('/games').then(r => setGames(r.data)).catch(() => {});
  };

  const filteredGames = labId
    ? games.filter(g => String(g.lab_id) === labId)
    : games;

  const openAdd = () => {
    setEditGame(null);
    setForm({ name: '', genre: 'action', exe_path: '', short_description: '', description: '', sort_order: 0 });
    setShowForm(true);
  };

  const openEdit = (g) => {
    setEditGame(g);
    setForm({ name: g.name, genre: g.genre, exe_path: g.exe_path || '', short_description: g.short_description || '', description: g.description || '', sort_order: g.sort_order || 0 });
    setShowForm(true);
  };

  const saveGame = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      if (editGame) {
        const r = await api.put(`/games/${editGame.id}`, form);
        setGames(prev => prev.map(g => g.id === editGame.id ? r.data : g));
      } else {
        const r = await api.post('/games', { ...form, lab_id: labId });
        setGames(prev => [...prev, r.data]);
      }
      setShowForm(false);
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally { setSaving(false); }
  };

  const toggleActive = async (id, current) => {
    try {
      const r = await api.put(`/games/${id}`, { is_active: !current });
      setGames(prev => prev.map(g => g.id === id ? r.data : g));
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const deleteGame = async (id) => {
    if (!window.confirm(t('deleteGameConfirm'))) return;
    try {
      await api.delete(`/games/${id}`);
      setGames(prev => prev.filter(g => g.id !== id));
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const openCatalog = async () => {
    if (!labId) return alert('Avval filial tanlang');
    setCatalogLoading(true);
    setCatalogModal(true);
    try {
      const r = await api.get('/game-catalog');
      setCatalogList(r.data || []);
      setCatalogSel({});
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
      setCatalogModal(false);
    } finally { setCatalogLoading(false); }
  };

  const doCatalogImport = async () => {
    const ids = Object.entries(catalogSel).filter(([, v]) => v).map(([k]) => parseInt(k));
    if (!ids.length) return alert('Hech narsa tanlanmagan');
    setCatalogImporting(true);
    try {
      const r = await api.post('/owner/games/from-catalog', { lab_id: parseInt(labId), catalog_ids: ids });
      alert(`✅ ${r.data.message || (r.data.created + ' ta o\'yin qo\'shildi')}`);
      setCatalogModal(false);
      loadGames();
    } catch (e) {
      alert(e.response?.data?.error || 'Xatolik');
    } finally { setCatalogImporting(false); }
  };

  const uploadCatalogCover = async (catalogId, file) => {
    setCatalogUploading(u => ({ ...u, [catalogId]: true }));
    try {
      const fd = new FormData();
      fd.append('cover', file);
      const r = await api.post(`/admin/game-catalog/${catalogId}/upload-cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setCatalogList(list => list.map(g => g.id === catalogId ? { ...g, cover_url: r.data.cover_url } : g));
    } catch (e) {
      alert(e.response?.data?.error || 'Yuklashda xatolik');
    } finally {
      setCatalogUploading(u => ({ ...u, [catalogId]: false }));
    }
  };

  const filteredCatalog = catalogList.filter(g => {
    if (catalogFilter.platform && g.platform !== catalogFilter.platform) return false;
    if (catalogFilter.genre && g.genre !== catalogFilter.genre) return false;
    if (catalogFilter.q) {
      const q = catalogFilter.q.toLowerCase();
      if (!g.name.toLowerCase().includes(q) && !(g.slug || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const catalogSelectedCount = Object.values(catalogSel).filter(Boolean).length;

  const uploadFile = async (gameId, field, file) => {
    if (!file) return;
    const maxSize = field === 'trailer' ? 200 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`${t('fileTooLarge')} ${field === 'trailer' ? '200MB' : '5MB'}`);
      return;
    }

    const fd = new FormData();
    fd.append(field, file);
    setUploading(u => ({ ...u, [`${gameId}-${field}`]: true }));
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/games/${gameId}/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error);
      setGames(prev => prev.map(g => g.id === gameId ? data : g));
    } catch (e) {
      alert(e.message || t('uploadError'));
    } finally {
      setUploading(u => ({ ...u, [`${gameId}-${field}`]: false }));
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{t('gamesTitle')}</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>
            {t('gamesSubtitle')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={openCatalog} style={{ background: 'rgba(124,58,237,0.12)', border: '1px solid var(--purple)', color: 'var(--purple)', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
            📚 Katalogdan qo'shish
          </button>
          <button onClick={openAdd} style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>
            {t('addGame')}
          </button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
            {editGame ? `${t('gameEditTitle')}: ${editGame.name}` : t('gameNewTitle')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 12, marginBottom: 12 }}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('gameName')}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }} />
            <select value={form.genre} onChange={e => setForm(f => ({ ...f, genre: e.target.value }))} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }}>
              {GENRES.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select>
            <input value={form.sort_order} onChange={e => setForm(f => ({ ...f, sort_order: e.target.value }))} placeholder={t('gameSort')} type="number"
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13 }} />
          </div>
          <input value={form.exe_path} onChange={e => setForm(f => ({ ...f, exe_path: e.target.value }))} placeholder={t('gameExePath')}
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12, fontFamily: 'monospace' }} />
          <input value={form.short_description} onChange={e => setForm(f => ({ ...f, short_description: e.target.value }))} placeholder={t('gameShortDesc')}
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13, marginBottom: 8 }} />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('gameFullDesc')}
            rows={4}
            style={{ width: '100%', boxSizing: 'border-box', background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '9px 12px', borderRadius: 8, fontSize: 13, marginBottom: 16, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={saveGame} disabled={saving} style={{ background: 'var(--cyan)', border: 'none', color: '#000', padding: '8px 24px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
              {saving ? '...' : (editGame ? t('save') : t('add'))}
            </button>
            <button onClick={() => setShowForm(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>{t('cancelShort')}</button>
          </div>
        </div>
      )}

      {filteredGames.length === 0 ? (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 56, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>🎮</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{t('noGames')}</div>
          <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('noGamesDesc')}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
          {filteredGames.map(g => (
            <div key={g.id} style={{ background: 'var(--card2)', border: `1px solid ${g.is_active ? 'var(--border)' : 'rgba(255,59,92,0.3)'}`, borderRadius: 14, overflow: 'hidden', opacity: g.is_active ? 1 : 0.7 }}>

              <div style={{ position: 'relative', height: 130, background: 'var(--bg2)', overflow: 'hidden' }}>
                {g.cover_path ? (
                  <img src={g.cover_path.startsWith('http') ? g.cover_path : `${BASE_URL}${g.cover_path}`} alt={g.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 40 }}>🎮</div>
                )}

                {g.trailer_path && (
                  <div
                    onClick={() => setPreview({ type: 'trailer', url: `${BASE_URL}${g.trailer_path}`, name: g.name })}
                    style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.75)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '3px 8px', fontSize: 11, cursor: 'pointer', color: '#fff' }}>
                    ▶ Trailer
                  </div>
                )}

                {!g.is_active && (
                  <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(255,59,92,0.85)', borderRadius: 6, padding: '2px 8px', fontSize: 10, color: '#fff' }}>{t('gameInactive')}</div>
                )}
              </div>

              <div style={{ padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{g.name}</div>
                  <span style={{ fontSize: 10, color: 'var(--text3)', background: 'rgba(255,255,255,0.06)', padding: '2px 7px', borderRadius: 10, whiteSpace: 'nowrap' }}>{genreLabel(g.genre)}</span>
                </div>

                {g.exe_path && (
                  <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'monospace', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={g.exe_path}>
                    📁 {g.exe_path}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: 10 }}>
                  <div>
                    <input ref={el => coverRefs.current[g.id] = el} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) uploadFile(g.id, 'cover', e.target.files[0]); e.target.value = ''; }} />
                    <button
                      onClick={() => coverRefs.current[g.id]?.click()}
                      disabled={uploading[`${g.id}-cover`]}
                      title="Karta rasmi — Steam header formati 460×215 px (2.14:1), JPG/PNG, max 500 KB"
                      style={{ width: '100%', padding: '6px 0', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', color: 'var(--text2)' }}>
                      {uploading[`${g.id}-cover`] ? '⏳ ...' : g.cover_path ? t('coverBtnDone') : t('coverBtn')}
                    </button>
                  </div>

                  <div>
                    <input ref={el => heroRefs.current[g.id] = el} type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) uploadFile(g.id, 'hero', e.target.files[0]); e.target.value = ''; }} />
                    <button
                      onClick={() => heroRefs.current[g.id]?.click()}
                      disabled={uploading[`${g.id}-hero`]}
                      title="Tema rasmi (yuqori katta panel) — 16:9 ratio, 1280×720 yoki 960×540 JPG/PNG, max 500 KB"
                      style={{ width: '100%', padding: '6px 0', borderRadius: 7, fontSize: 11, cursor: 'pointer',
                        background: g.hero_image_path ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${g.hero_image_path ? 'var(--cyan)' : 'var(--border)'}`,
                        color: g.hero_image_path ? 'var(--cyan)' : 'var(--text2)' }}>
                      {uploading[`${g.id}-hero`] ? '⏳ ...' : g.hero_image_path ? t('heroBtnDone') : t('heroBtn')}
                    </button>
                  </div>

                  <div>
                    <input ref={el => trailerRefs.current[g.id] = el} type="file" accept="video/mp4,video/webm" style={{ display: 'none' }}
                      onChange={e => { if (e.target.files[0]) uploadFile(g.id, 'trailer', e.target.files[0]); e.target.value = ''; }} />
                    <button
                      onClick={() => trailerRefs.current[g.id]?.click()}
                      disabled={uploading[`${g.id}-trailer`]}
                      title="Trailer — MP4 16:9 (480×270 yoki 640×360), audio yo'q, max 5 MB"
                      style={{ width: '100%', padding: '6px 0', borderRadius: 7, fontSize: 11, cursor: 'pointer',
                        background: g.trailer_path ? 'rgba(124,58,237,0.12)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${g.trailer_path ? 'var(--purple)' : 'var(--border)'}`,
                        color: g.trailer_path ? 'var(--purple)' : 'var(--text2)' }}>
                      {uploading[`${g.id}-trailer`] ? '⏳ ...' : g.trailer_path ? t('trailerBtnDone') : t('trailerBtn')}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => openEdit(g)} style={{ flex: 1, padding: '5px 0', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: 'rgba(0,212,255,0.08)', border: '1px solid var(--cyan)', color: 'var(--cyan)' }}>
                    {t('editGameBtn')}
                  </button>
                  <button onClick={() => toggleActive(g.id, g.is_active)} style={{ padding: '5px 10px', borderRadius: 7, fontSize: 11, cursor: 'pointer',
                    background: g.is_active ? 'rgba(255,59,92,0.08)' : 'rgba(0,255,136,0.08)',
                    border: `1px solid ${g.is_active ? 'var(--red)' : 'var(--green)'}`,
                    color: g.is_active ? 'var(--red)' : 'var(--green)' }}>
                    {g.is_active ? '⏸' : '▶'}
                  </button>
                  <button onClick={() => deleteGame(g.id)} style={{ padding: '5px 10px', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: 'rgba(255,59,92,0.08)', border: '1px solid var(--red)', color: 'var(--red)' }}>
                    🗑
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div onClick={e => e.stopPropagation()} style={{ maxWidth: 800, width: '90%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{preview.name}</div>
              <button onClick={() => setPreview(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text2)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            {preview.type === 'trailer' ? (
              <video src={preview.url} controls autoPlay muted
                style={{ width: '100%', borderRadius: 12, maxHeight: '60vh' }} />
            ) : (
              <img src={preview.url} alt="cover" style={{ width: '100%', borderRadius: 12, maxHeight: '60vh', objectFit: 'contain' }} />
            )}
          </div>
        </div>
      )}

      {/* Katalog Modal — Global game catalog */}
      {catalogModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, width: 800, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 16 }}>📚 O'yinlar katalogi</h3>
              <button onClick={() => setCatalogModal(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text2)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>

            {catalogLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Yuklanmoqda...</div>
            ) : (
              <>
                {/* Filters */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
                  <input
                    value={catalogFilter.q}
                    onChange={e => setCatalogFilter(f => ({ ...f, q: e.target.value }))}
                    placeholder="🔍 Qidiruv (nom yoki slug)"
                    style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
                  />
                  <select
                    value={catalogFilter.platform}
                    onChange={e => setCatalogFilter(f => ({ ...f, platform: e.target.value }))}
                    style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
                  >
                    <option value="">Barcha platformalar</option>
                    <option value="steam">Steam</option>
                    <option value="epic">Epic Games</option>
                    <option value="riot">Riot</option>
                    <option value="battlenet">Battle.net</option>
                    <option value="ea">EA</option>
                    <option value="direct">Direct/Standalone</option>
                  </select>
                  <select
                    value={catalogFilter.genre}
                    onChange={e => setCatalogFilter(f => ({ ...f, genre: e.target.value }))}
                    style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 13 }}
                  >
                    <option value="">Barcha janrlar</option>
                    <option value="fps">FPS</option>
                    <option value="moba">MOBA</option>
                    <option value="battle-royale">Battle Royale</option>
                    <option value="rpg">RPG</option>
                    <option value="sports">Sports</option>
                    <option value="strategy">Strategy</option>
                    <option value="action">Action</option>
                    <option value="sandbox">Sandbox</option>
                    <option value="simulation">Simulation</option>
                    <option value="party">Party</option>
                    <option value="social">Social</option>
                    <option value="survival">Survival</option>
                    <option value="card">Card</option>
                  </select>
                </div>

                <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>
                  {filteredCatalog.length} ta o'yin ko'rsatilmoqda ({catalogList.length} dan)
                  <span style={{ marginLeft: 12, color: 'var(--cyan)', cursor: 'pointer' }}
                    onClick={() => { const s = {}; filteredCatalog.forEach(g => s[g.id] = true); setCatalogSel(s); }}>
                    Hammasini tanlash
                  </span>
                  {' / '}
                  <span style={{ color: 'var(--text3)', cursor: 'pointer' }}
                    onClick={() => setCatalogSel({})}>
                    Bekor qilish
                  </span>
                </div>

                <div style={{ overflowY: 'auto', flex: 1, marginBottom: 14, border: '1px solid var(--border)', borderRadius: 8 }}>
                  {filteredCatalog.length === 0 ? (
                    <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>
                      Hech narsa topilmadi
                    </div>
                  ) : filteredCatalog.map(g => (
                    <label key={g.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                      borderBottom: '1px solid var(--border)', cursor: 'pointer',
                      background: catalogSel[g.id] ? 'rgba(124,58,237,0.08)' : 'transparent',
                    }}>
                      <input
                        type="checkbox"
                        checked={!!catalogSel[g.id]}
                        onChange={e => setCatalogSel(s => ({ ...s, [g.id]: e.target.checked }))}
                      />
                      <div style={{ position: 'relative', flexShrink: 0 }}>
                        {g.cover_url ? (
                          <img src={g.cover_url.startsWith('http') ? g.cover_url : `${BASE_URL}${g.cover_url}`} alt={g.name}
                            style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4, background: 'var(--bg2)', display: 'block' }}
                            onError={e => { e.target.style.display = 'none'; }} />
                        ) : (
                          <div style={{ width: 60, height: 40, background: 'var(--bg2)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>🎮</div>
                        )}
                        {isSuperAdmin() && (
                          <label onClick={e => e.stopPropagation()} title="Rasm yuklash" style={{
                            position: 'absolute', bottom: 0, right: 0, background: 'rgba(0,0,0,0.7)',
                            borderRadius: 3, padding: '1px 3px', cursor: 'pointer', fontSize: 10, lineHeight: 1,
                          }}>
                            {catalogUploading[g.id] ? '⏳' : '📷'}
                            <input type="file" accept="image/*" style={{ display: 'none' }}
                              onChange={e => e.target.files[0] && uploadCatalogCover(g.id, e.target.files[0])} />
                          </label>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 3 }}>{g.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--text3)', display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                          {g.platform && (
                            <span style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80', padding: '1px 6px', borderRadius: 4 }}>
                              {g.platform}
                            </span>
                          )}
                          {g.genre && (
                            <span style={{ background: 'rgba(0,212,255,0.12)', color: 'var(--cyan)', padding: '1px 6px', borderRadius: 4 }}>
                              {g.genre}
                            </span>
                          )}
                          {g.age_rating > 0 && (
                            <span style={{ background: g.age_rating >= 18 ? 'rgba(255,59,92,0.15)' : 'rgba(255,200,0,0.15)', color: g.age_rating >= 18 ? '#ff3b5c' : '#ffc800', padding: '1px 6px', borderRadius: 4 }}>
                              {g.age_rating}+
                            </span>
                          )}
                          {g.publisher && (
                            <span style={{ color: 'var(--text3)' }}>{g.publisher}</span>
                          )}
                        </div>
                        {(lang === 'ru' ? g.description_ru : g.description) && (
                          <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {lang === 'ru' ? g.description_ru : g.description}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setCatalogModal(false)} style={{ flex: 1, background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: 10, borderRadius: 8, cursor: 'pointer' }}>Bekor</button>
                  <button onClick={doCatalogImport} disabled={catalogImporting || !catalogSelectedCount} style={{
                    flex: 2, background: 'rgba(124,58,237,0.2)', border: '1px solid var(--purple)',
                    color: 'var(--purple)', padding: 10, borderRadius: 8, cursor: 'pointer', fontWeight: 700,
                    opacity: (catalogImporting || !catalogSelectedCount) ? 0.5 : 1,
                  }}>
                    {catalogImporting ? 'Qo\'shilmoqda...' : `📚 ${catalogSelectedCount} ta o'yinni qo'shish`}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 24, background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.3)', borderRadius: 12, padding: 16, fontSize: 12, color: 'var(--text2)' }}>
        <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--purple)' }}>📖 Tavsiya etilgan formatlar</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: 8 }}>
            <div style={{ color: 'var(--text)', marginBottom: 4 }}>🖼️ <b>Karta rasmi (Cover)</b></div>
            <div>Pastdagi o'yin kartalarida</div>
            <div>• <b>460 × 215 px</b> (Steam header, 2.14:1)</div>
            <div>• JPG/PNG, max 500 KB</div>
            <div>• Steam'dan: <code style={{ color: 'var(--cyan)', fontSize: 10 }}>header.jpg</code></div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: 8 }}>
            <div style={{ color: 'var(--text)', marginBottom: 4 }}>🎨 <b>Tema rasmi (Hero)</b></div>
            <div>Yuqori katta panelda</div>
            <div>• <b>1280 × 720 px</b> (16:9, HD)</div>
            <div>• Yoki 960×540, 640×360</div>
            <div>• JPG/PNG, max 500 KB</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: 8 }}>
            <div style={{ color: 'var(--text)', marginBottom: 4 }}>🎬 <b>Trailer (MP4)</b></div>
            <div>Hero panel ichida avtomatik o'ynaydi</div>
            <div>• <b>480 × 270</b> yoki 640×360 (16:9)</div>
            <div>• 30-60 sek, audio yo'q (mute)</div>
            <div>• H.264, max 5 MB</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.03)', padding: '10px 12px', borderRadius: 8 }}>
            <div style={{ color: 'var(--text)', marginBottom: 4 }}>📁 <b>Exe yo'li</b></div>
            <div>Windows'dagi to'liq yo'l:</div>
            <code style={{ color: 'var(--cyan)', fontSize: 11 }}>C:\Games\CS2\cs2.exe</code>
            <div style={{ marginTop: 4 }}>• <b>Tartib:</b> 0 birinchi, 1, 2...</div>
          </div>
        </div>
        <div style={{ marginTop: 10, padding: '8px 12px', background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 8 }}>
          💡 <b>Tavsiya:</b> Yon tomonlar bo'sh qolmasligi uchun tema rasmi <b>16:9</b> aspect bo'lishi shart.
          Boshqa aspect ratio'lar (kvadrat, vertikal) ham qabul qilinadi — orqa fonda blur bilan to'ldiriladi.
        </div>
      </div>
    </div>
  );
}
